import { EModelProvider, type IModel as IApiModel } from "../types";

// NOTE: NSAI-4511 — provider-label derivation shared by web + mobile (their
// modelParser copies were byte-identical). Only the LABELS are shared here —
// the matching icon components are React / React Native and stay in each app's
// modelParser (frontend-shared must remain runtime-agnostic).
// NOTE: NSAI-5056 — this used to brand GLM and Kimi by a `startsWith("glm")` /
// `startsWith("kimi")` match on the model NAME, because their rows carried
// provider "openai" and would otherwise have rendered as "Openai". They now
// carry their real vendor, so the label comes from the provider like every
// other model, and the name is no longer consulted at all. Keyed on the enum
// value so a catalog row whose provider predates this table falls through to
// the capitalized slug rather than throwing.
const PROVIDER_LABELS: Partial<Record<EModelProvider, string>> = {
  [EModelProvider.MOONSHOT]: "Kimi",
  [EModelProvider.ZAI]: "GLM",
};

// NOTE: NSAI-7770 — the two labels that are not a vendor's name. They double
// as group keys (picker groups, the provider rail, PROVIDER_PRIORITY), so they
// stay stable English strings here and are translated only where they are
// shown — see `translateProviderLabel`.
export const AUTO_PROVIDER_LABEL = "Auto";
export const ON_PREM_PROVIDER_LABEL = "On-Prem AI Model";

const PROVIDER_LABEL_I18N_KEYS: Record<string, string> = {
  [AUTO_PROVIDER_LABEL]: "chat:autoModel.label",
  [ON_PREM_PROVIDER_LABEL]: "chat:model_picker.on_prem_group",
};

export const getProviderLabel = (
  model: Pick<IApiModel, "provider" | "hostingProvider"> &
    Partial<Pick<IApiModel, "isRouterModel">>,
): string => {
  // NOTE: NSAI-7770 — Auto (the router model) is catalogued as a NodeShift-
  // hosted `selfhosted` row, so it used to fall into the on-prem group. It
  // answers through cloud vendors' models, so it is its own group instead.
  if (model.isRouterModel === true) {
    return AUTO_PROVIDER_LABEL;
  }

  if (model.hostingProvider.toLowerCase() === "nodeshift") {
    return ON_PREM_PROVIDER_LABEL;
  }

  const providerLabel = PROVIDER_LABELS[model.provider];
  if (providerLabel) {
    return providerLabel;
  }

  return model.provider.charAt(0).toUpperCase() + model.provider.slice(1);
};

interface ITranslateProviderLabelProps {
  providerLabel: string;
  t: (key: string) => string;
}

// NOTE: NSAI-7770 — vendor names are brands and stay as they are; only the two
// synthetic labels above go through i18n. Keys are namespace-qualified so the
// caller's `t` works whichever namespace it was bound to.
export const translateProviderLabel = ({
  providerLabel,
  t,
}: ITranslateProviderLabelProps): string => {
  const key = PROVIDER_LABEL_I18N_KEYS[providerLabel];
  return key ? t(key) : providerLabel;
};
